
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const NotificationContext = createContext();

export const useNotifications = () => {
  return useContext(NotificationContext);
};

export const NotificationProvider = ({ children }) => {
  // Initialize state lazily from localStorage to avoid overwriting on mount
  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem('notifications');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Failed to load notifications", e);
      return [];
    }
  });
  
  const [unreadCount, setUnreadCount] = useState(0);

  // Persist notifications whenever they change
  useEffect(() => {
    setUnreadCount(notifications.filter(n => !n.read).length);
    
    try {
      // Limit to last 50 items to prevent QuotaExceededError
      const limitedNotifications = notifications.slice(0, 50);
      localStorage.setItem('notifications', JSON.stringify(limitedNotifications));
    } catch (error) {
      console.error("Failed to save notifications to localStorage:", error);
      // If quota exceeded, try to save a smaller batch
      if (error.name === 'QuotaExceededError' || error.code === 22) {
        try {
           const emergencyBatch = notifications.slice(0, 10);
           localStorage.setItem('notifications', JSON.stringify(emergencyBatch));
        } catch (e) {
           console.error("Critical storage failure - clearing notifications", e);
           // Last resort: clear to allow app to function
           localStorage.removeItem('notifications');
        }
      }
    }
  }, [notifications]);

  // Wrap in useCallback to ensure stability and prevent infinite loops in consumers
  const addNotification = useCallback((title, description, type = 'info') => {
    setNotifications(prev => {
      const newNotification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        title,
        description,
        type,
        timestamp: new Date().toISOString(),
        read: false
      };
      // Add new to top
      return [newNotification, ...prev];
    });
  }, []);

  const markAsRead = useCallback((id) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(n => ({ ...n, read: true }))
    );
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearAll
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
